import React from "react";
import "./Project.css";

import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import transition from "../../transition";

import projects from "../../data/projects";
import SitePreview from "../shared/SitePreview";
import Seo from "../../seo/Seo";
import { breadcrumbJsonLd, projectJsonLd } from "../../seo/site";
import { useLang, useTranslations, langPath } from "../../i18n";
import { localizeProject } from "../../i18n/projectContent";

const ease = [0.83, 0, 0.17, 1];

const Project = () => {
  const { slug } = useParams();
  const lang = useLang();
  const t = useTranslations();
  const index = projects.findIndex((p) => p.slug === slug);

  // unknown project → send the visitor back to the work list
  if (index === -1) {
    return <Navigate to={langPath("/projects", lang)} replace />;
  }

  const project = localizeProject(projects[index], lang);
  const prev = projects[(index - 1 + projects.length) % projects.length];
  const next = projects[(index + 1) % projects.length];

  return (
    <>
      <Seo
        lang={lang}
        path={`/projects/${project.slug}`}
        title={project.name}
        description={project.description}
        type="article"
        jsonLd={[
          breadcrumbJsonLd([
            { name: t.nav.index, path: `/${lang}` },
            { name: t.nav.work, path: `/${lang}/projects` },
            { name: project.name, path: `/${lang}/projects/${project.slug}` },
          ]),
          projectJsonLd(project),
        ]}
      />
      <div className="bg"></div>
      <h1 className="visually-hidden">{project.name}</h1>

      <Link to={langPath("/projects", lang)}>
        <motion.div
          className="backButton"
          initial={{
            scale: 0,
            transition: { duration: 1, ease, delay: 1.5 },
          }}
          animate={{
            scale: 1,
            transition: { duration: 1.5, ease, delay: 0.5 },
          }}
        >
          <div className="back">
            <p>&#8592;</p>
          </div>
        </motion.div>
      </Link>
      <div className="project-container">
        <div className="project-info">
          <div className="project-preview">
            <SitePreview
              src={project.preview}
              url={project.url}
              alt={project.name}
            />
          </div>
          <motion.div
            className="project-description"
            initial={{
              x: -40,
              opacity: 0,
              transition: { duration: 1, ease, delay: 0.25 },
            }}
            animate={{
              x: 0,
              opacity: 1,
              transition: { duration: 1.5, ease, delay: 0.25 },
            }}
          >
            <p>
              <b>{project.name}</b>
            </p>
            <p>{project.description}</p>
            <div className="project-link-out">
              <a href={project.url} target="_blank" rel="noreferrer">
                {t.project.visit} &#8599;
              </a>
            </div>
          </motion.div>
        </div>
        <div className="footer">
          <motion.div
            className="divider"
            initial={{ width: 0, transition: { duration: 1, ease, delay: 1 } }}
            animate={{
              width: "100%",
              transition: { duration: 1.5, ease, delay: 1 },
            }}
          ></motion.div>
          <motion.div
            className="footer-content"
            initial={{
              y: 200,
              opacity: 0,
              transition: { duration: 1, ease, delay: 2 },
            }}
            animate={{
              y: 0,
              opacity: 1,
              transition: { duration: 2, ease, delay: 0.5 },
            }}
          >
            <div className="footer-col footer-nav">
              <Link
                to={langPath(`/projects/${prev.slug}`, lang)}
                aria-label={`${t.project.prev}: ${prev.name}`}
              >
                <div className="arrow">
                  <p>&larr;</p>
                </div>
              </Link>
              <Link
                to={langPath(`/projects/${next.slug}`, lang)}
                aria-label={`${t.project.next}: ${next.name}`}
              >
                <div className="arrow">
                  <p>&rarr;</p>
                </div>
              </Link>
            </div>
            <div className="footer-col">
              <p>
                {t.project.category}
                <br /> {project.category}
              </p>
            </div>
            <div className="footer-col">
              <p>
                {t.project.role} <br /> {project.role}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default transition(Project);
